import React from 'react';
import SectionCard from './SectionCard';
import { useAppContext } from '../context/AppContext';
import { skippedNoteText } from '../ai/rows/prepareRows';

const SERVICE_LABELS = {
  mattergen: 'MatterGen',
  fm4m:      'IBM FM4M',
  gt4sd:     'GT4SD',
};

const METHOD_COLORS = {
  family_tag:        '#4ade80',  // green  — LLM got it right
  keyword_inference: '#fb923c',  // orange — needed fallback inference
  default:           '#94a3b8',  // slate  — fell all the way to default
};

const METHOD_LABELS = {
  family_tag:        'family tag',
  keyword_inference: 'keyword inference',
  default:           'default',
};

const BUDGET_OPTIONS = [8, 16, 32, 64, 128, 256, 512, 1024];

/**
 * The count menu for one step (spec 2026-10-07, 0.4): the base-8 ladder capped at the plan
 * max, plus the step's current count when it is not a ladder value (a router split such as
 * 21), so the select always shows the real number. A missing plan max means the full ladder.
 * 1b replaces this table. The router's rows are fitted at Route (prefillRows), and the
 * prepareRows backstop fits again at Execute when an edit went over the max.
 */
export function budgetOptionsFor(planMax, current) {
  const max = Number(planMax) > 0 ? Number(planMax) : BUDGET_OPTIONS[BUDGET_OPTIONS.length - 1];
  const options = BUDGET_OPTIONS.filter((n) => n <= max);
  if (options.length === 0) options.push(max);
  const cur = Number(current);
  if (Number.isFinite(cur) && cur > 0 && cur <= max && !options.includes(cur)) options.push(cur);
  return options.sort((a, b) => a - b);
}

/**
 * The table's steps, read from the review (editable-plan Part 1a, 2.3). One step per row:
 * the service, the row id a count edit targets, the row's domain, its target and its count.
 * The target says " (custom)" for a custom model, and " (scaled to fit from N)" or else
 * " (rounded from N)" when the router's fit or snap changed the count. The router's skipped
 * entries ride on the first step, or on one blocked step when no row is left. 1b replaces
 * this table and deletes this adapter.
 */
export function stepsFromReview(review) {
  const rows = Array.isArray(review?.rows) ? review.rows : [];
  const skipped = Array.isArray(review?.routerSnapshot?.skipped) ? review.routerSnapshot.skipped : [];
  const meta = review?.spec?.meta || {};
  const resolution = {
    method: meta.resolution_method || null,
    notes: meta.resolution_notes || null,
    domains: meta.resolved_domains || [],
  };
  const steps = rows.map((row) => {
    const adjustments = row.adjustments || {};
    let variant = `${row.target}${row.customModelId ? ' (custom)' : ''}`;
    if (Number.isInteger(adjustments.scaledFrom)) variant += ` (scaled to fit from ${adjustments.scaledFrom})`;
    else if (Number.isInteger(adjustments.roundedFrom)) variant += ` (rounded from ${adjustments.roundedFrom})`;
    return { kind: row.service, rowId: row.id, label: row.domain, variant, count: row.candidates, resolution };
  });
  if (steps.length === 0) {
    return skipped.length > 0
      ? [{ kind: 'blocked', label: 'No enabled model can serve this objective', skipped, resolution }]
      : [];
  }
  if (skipped.length > 0) steps[0] = { ...steps[0], skipped };
  return steps;
}

function ResolutionBadge({ resolution }) {
  if (!resolution?.method) return null;
  const color = METHOD_COLORS[resolution.method] || '#94a3b8';
  const label = METHOD_LABELS[resolution.method] || resolution.method;
  return (
    <div style={{ marginBottom: 10, padding: '7px 12px', background: '#0a1120', borderRadius: 6, borderLeft: `3px solid ${color}`, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      <div style={{ flexShrink: 0 }}>
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color, background: `${color}1a`, border: `1px solid ${color}44`, borderRadius: 4, padding: '2px 6px' }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.5 }}>
        <span style={{ color: '#cbd5e1' }}>Domains: </span>
        {(resolution.domains || []).map(d => (
          <span key={d} style={{ marginRight: 6, color: '#e2e8f0', background: '#1e293b', borderRadius: 4, padding: '1px 6px', fontSize: 11 }}>{d}</span>
        ))}
        {resolution.notes && (
          <span style={{ marginLeft: 4, color: '#64748b' }}>— {resolution.notes}</span>
        )}
      </div>
    </div>
  );
}

// Org model policy notes (2026-08-22): a model the org disabled (or weighted
// to zero) is absent from the table; say so, and name the fallback. Part 1a:
// a model the plan max left no room for gets its own line, and every line is
// skippedNoteText (prepareRows.js), the same sentence a refused Re-run shows.
function PolicyNotes({ skipped }) {
  if (!skipped?.length) return null;
  return (
    <div style={{ marginBottom: 10, padding: '7px 12px', background: '#0a1120', borderRadius: 6, borderLeft: '3px solid #f59e0b', fontSize: 11, color: '#94a3b8', lineHeight: 1.5 }}>
      {skipped.map((s, i) => (
        <div key={i} title={s.reason === 'plan_max' ? undefined : 'Set under Admin Portal, Models'}>{skippedNoteText(s)}</div>
      ))}
    </div>
  );
}

function PlannedStepsTable({ plannedSteps, onExecute, onClear, onViewPlan, onPreviewSelection }) {
  const { setRowCandidates, planLimits } = useAppContext() || {};
  const planMax = Number(planLimits?.maxCandidatesPerRun) > 0 ? Number(planLimits.maxCandidatesPerRun) : null;
  // All steps share the same resolution metadata — show it once above the table
  const resolution = plannedSteps[0]?.resolution || null;
  const skipped = plannedSteps.find(s => Array.isArray(s.skipped) && s.skipped.length)?.skipped || [];
  const blocked = plannedSteps.some(s => s.kind === 'blocked');

  // Part 1a (2.3): a count edit is a row edit. setRowCandidates dispatches
  // SET_ROW_CANDIDATES for the step's row (stepsFromReview), and the debounced draft
  // patch persists it.
  const handleBudgetChange = (step, value) => {
    if (typeof setRowCandidates === 'function') setRowCandidates(step.rowId, Number(value));
  };

  // The row's count.
  const effectiveBudget = (s) => s.count;
  // What the menu shows: never above the plan max (a draft resumed after a plan change can
  // carry a larger count; the Execute backstop fits it to the max anyway).
  const shownBudget = (s) => {
    const b = Number(effectiveBudget(s));
    return planMax != null ? Math.min(b, planMax) : b;
  };

  return (
    <SectionCard
      title="Planned Steps"
      actions={(onViewPlan || onPreviewSelection) ? (
        <div className="d-flex gap-2">
          {onViewPlan && (<button className="btn btn-outline-light btn-sm" onClick={onViewPlan}>View plan JSON</button>)}
          {onPreviewSelection && (<button className="btn btn-outline-info btn-sm" onClick={onPreviewSelection}>Preview selection</button>)}
        </div>
      ) : null}
    >
      <ResolutionBadge resolution={resolution} />
      <PolicyNotes skipped={skipped} />

      <div className="table-responsive">
        <table className="table table-dark table-striped table-hover align-middle mb-0 text-light">
          <thead>
            <tr>
              <th>Tool</th>
              <th>Domain</th>
              <th>Variant</th>
              <th>Candidates</th>
            </tr>
          </thead>
          <tbody>
            {plannedSteps.map((s, i) => {
              if (s.kind === 'blocked') {
                return (
                  <tr key="blocked">
                    <td colSpan={4} style={{ color: '#f59e0b' }}>
                      No enabled model can serve this objective. Ask your organization administrator to enable a model under Admin Portal, Models.
                    </td>
                  </tr>
                );
              }
              return (
                <tr key={i}>
                  <td>{SERVICE_LABELS[s.kind] || s.kind}</td>
                  <td>{s.label}</td>
                  <td>{s.variant || '—'}</td>
                  <td>
                    <select
                      className="form-select form-select-sm bg-dark text-light"
                      style={{ width: 130 }}
                      value={shownBudget(s)}
                      onChange={(e) => handleBudgetChange(s, e.target.value)}
                    >
                      {budgetOptionsFor(planMax, shownBudget(s)).map(n => (
                        <option key={n} value={n}>{n} candidates</option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="d-flex gap-2 mt-3">
        <button className="btn btn-primary btn-sm" onClick={onExecute} disabled={blocked} title={blocked ? 'No enabled model can serve this objective' : undefined}>Execute planned steps</button>
        <button className="btn btn-outline-light btn-sm" onClick={onClear}>Clear preview</button>
      </div>
    </SectionCard>
  );
}

export default PlannedStepsTable;
